import React, { useState, useEffect } from "react";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import "./App.css";
import BottomNavBar, { FormDirtyContext } from "./components/BottomNavBar";
import ClientDetails from "./components/ClientDetails";
import ClientListComponent from "./components/ClientList";
import ConfigureTestServer from "./components/ConfigureTestServer";
import CreateClient from "./components/CreateClient";
import EditClient from "./components/EditClient";
import EditUser from "./components/EditUser";
import ExerciseFeedbackList from "./components/ExerciseFeedbackList";
import InactivityMonitor from "./components/InactivityMonitor";
import IOSInstallInstructions from "./components/IOSInstallInstructions";
import LoggedOutNotice from "./components/LoggedOutNotice";
import Login from "./components/Login";
import Signup from "./components/Signup";
import SignupPathway from "./components/SignupPathway";
import ManageUsers from "./components/ManageUsers";
import MetricsDashboard from "./components/MetricsDashboard";
import PasswordResetConfirmComponent from "./components/PasswordResetConfirmation";
import ProgramDetails from "./components/ProgramDetails";
import ProgramForm from "./components/ProgramForm";
import ProgramListComponent from "./components/ProgramList";
import ProtectedRoute from "./components/ProtectedRoute";
import ResetPasswordComponent from "./components/ResetPassword";
import Terms from "./components/Terms";
import TherapistWalkthrough from "./components/TherapistWalkthrough";
import TopNavBar from "./components/TopNavBar";
import UserDetails from "./components/UserDetails";
import authService from "./services/authService";
import { usePWAInstallPrompt } from "./hooks/usePWAInstallPrompt";
import {
	hasDismissedInstallPrompt,
	persistInstallPromptDismissal,
} from "./utils/installPromptStorage";

const isPublicAuthPath = (pathname) =>
	pathname === "/" ||
	pathname === "/signup" ||
	pathname === "/signup/" ||
	pathname === "/start-trial" ||
	pathname === "/start-trial/" ||
	pathname === "/reset-password" ||
	pathname === "/logged-out" ||
	pathname.startsWith("/reset-password/") ||
	pathname.startsWith("/set-password/");

const App = () => {
	const [isDirty, setIsDirty] = useState(false);
	const [changes, setChanges] = useState([]);

	return (
		<FormDirtyContext.Provider value={{ isDirty, setIsDirty, changes, setChanges }}>
			<Router>
				<AppContent />
			</Router>
		</FormDirtyContext.Provider>
	);
};

const AppContent = () => {
	const isAuthenticated = authService.isAuthenticated();
	const isTherapist = authService.isTherapist();
	const location = useLocation();
	const pathname = location.pathname.replace(/\/+$/, "") || "/";
	const isPublicAuthPage = isPublicAuthPath(pathname);
	const isTermsPage = pathname === "/terms";
	const isLegalReviewPage =
		isTermsPage && new URLSearchParams(location.search).get("view") === "1";
	const hideBottomNav = isPublicAuthPage || (isTermsPage && !isLegalReviewPage);

	const [hasCheckedInstallDismissal, setHasCheckedInstallDismissal] = useState(false);
	const [isInstallPromptDismissed, setIsInstallPromptDismissed] = useState(false);
	const [showIOSInstallInstructions, setShowIOSInstallInstructions] = useState(false);

	const {
		isInstallable,
		isInstalled,
		iosBrowser,
		requiresManualInstall,
		promptInstall,
	} = usePWAInstallPrompt();

	useEffect(() => {
		setIsInstallPromptDismissed(hasDismissedInstallPrompt());
		setHasCheckedInstallDismissal(true);
	}, []);

	useEffect(() => {
		if (!("serviceWorker" in navigator)) return;

		navigator.serviceWorker.register("/service-worker.js").catch((error) => {
			console.error("Service worker registration failed:", error);
		});
	}, []);


	const dismissInstallPrompt = () => {
		persistInstallPromptDismissal();
		setIsInstallPromptDismissed(true);
	};

	const handleInstall = async () => {
		if (isInstallable) {
			return await promptInstall();
		}

		if (requiresManualInstall) {
			setShowIOSInstallInstructions(true);
			return { outcome: "manual" };
		}

		alert("Install prompt is not available in this local/dev context.");
		return { outcome: null };
	};

	const showInstallPrompt =
		hasCheckedInstallDismissal &&
		isAuthenticated &&
		!isTherapist &&
		!isPublicAuthPage &&
		(!isTermsPage || isLegalReviewPage) &&
		!isInstalled &&
		!isInstallPromptDismissed &&
		(isInstallable || requiresManualInstall || process.env.NODE_ENV === "development");

	return (
		<div className="min-vh-100 d-flex flex-column">
			<title>Plasticise - Login</title>
			{!isPublicAuthPage && <TopNavBar showNotifications={!isTermsPage} />}
			<div className="flex-grow-1 p-0 p-md-3" style={{ paddingBottom: "100px" }}>
				{isAuthenticated && !isPublicAuthPage && <InactivityMonitor />}
				{showInstallPrompt && (
					<div
						className="pwa-install-banner"
						role="region"
						aria-label="Install Plasticise"
					>
						<button
							type="button"
							className="pwa-install-button"
							onClick={handleInstall}
						>
							Install Plasticise
						</button>
						<button
							type="button"
							className="pwa-install-close-button"
							onClick={dismissInstallPrompt}
							aria-label="Dismiss install prompt"
							title="Not now"
						>
							&times;
						</button>
					</div>
				)}
				<Routes>
					<Route path="/" element={<Login />} />
					<Route path="/start-trial" element={<SignupPathway />} />
					<Route path="/signup" element={<Signup />} />
					<Route path="/metrics" element={<MetricsDashboard />} />
					<Route
						path="/manage"
						element={
							<ProtectedRoute allowedRoles={"staff"}>
								<ManageUsers />
							</ProtectedRoute>
						}
					/>
					<Route path="/reset-password" element={<ResetPasswordComponent />} />
					<Route path="/logged-out" element={<LoggedOutNotice />} />
					<Route
						path="/exercise/:exerciseId/feedback"
						element={
							<ProtectedRoute allowedRoles={"authenticated"}>
								<ExerciseFeedbackList />
							</ProtectedRoute>
						}
					/>

					<Route
						path="/reset-password/:uid/:token"
						element={<PasswordResetConfirmComponent />}
					/>
					<Route
						path="/set-password/:uid/:token"
						element={<PasswordResetConfirmComponent />}
					/>
					<Route
						path="/clients"
						element={
							<ProtectedRoute allowedRoles={"therapist"}>
								<ClientListComponent />
							</ProtectedRoute>
						}
					/>
					<Route path="/programs" element={<ProgramListComponent />} />
					<Route
						path="/client/create"
						element={
							<ProtectedRoute allowedRoles={"therapist"}>
								<CreateClient />
							</ProtectedRoute>
						}
					/>
					<Route path="/client/:clientId" element={<ClientDetails />} />
					<Route path="/program/:programId" element={<ProgramDetails />} />
					<Route path="/client/edit/:clientId" element={<EditClient />} />
					<Route
						path="/program/edit/:clientId/:programId"
						element={
							<ProtectedRoute allowedRoles={"therapist"}>
								<ProgramForm />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/user/:userId/edit"
						element={
							<ProtectedRoute allowedRoles={"authenticated"}>
								<EditUser />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/user/:userId"
						element={
							<ProtectedRoute allowedRoles={"authenticated"}>
								<UserDetails
									isInstallable={isInstallable}
									isInstalled={isInstalled}
									requiresManualInstall={requiresManualInstall}
									onInstall={handleInstall}
								/>
							</ProtectedRoute>
						}
					/>
					<Route
						path="/program/add/:clientId"
						element={
							<ProtectedRoute allowedRoles={"therapist"}>
								<ProgramForm />
							</ProtectedRoute>
						}
					/>
					<Route
						path="/program/duplicate/:clientId/:programId"
						element={
							<ProtectedRoute allowedRoles={"therapist"}>
								<ProgramForm />
							</ProtectedRoute>
						}
					/>
					<Route path="/terms" element={<Terms />} />
					<Route
						path="/configure-test-server"
						element={
							<ProtectedRoute allowedRoles={"staff"}>
								<ConfigureTestServer />
							</ProtectedRoute>
						}
					/>
				</Routes>
			</div>
			{isAuthenticated && !hideBottomNav && <BottomNavBar />}
			{isAuthenticated &&
				isTherapist &&
				!isPublicAuthPage &&
				location.pathname !== "/terms" && (
					<TherapistWalkthrough />
				)}
			<IOSInstallInstructions
				show={showIOSInstallInstructions}
				onHide={() => setShowIOSInstallInstructions(false)}
				browser={iosBrowser}
			/>
		</div>
	);
};

export default App;